import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSelectionList } from '@angular/material/list';
import { CountryNamedLocation } from '@microsoft/microsoft-graph-types-beta';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { SnackbarService } from 'projects/angular-clarity/src/app/services/snackbar.service';
import {
    createNamedLocation,
    createNamedLocationFailure,
    createNamedLocationSuccess,
    deleteNamedLocation,
    deleteNamedLocationFailure,
    deleteNamedLocationSuccess,
    updateNamedLocation,
    updateNamedLocationFailure,
    updateNamedLocationSuccess,
} from 'projects/angular-clarity/src/app/stores/client/graph/named-locations/actions';
import { selectIso3166_2CountryCodes } from 'projects/angular-clarity/src/app/stores/root.store';
import { loadIso3166_2CountryCodes } from 'projects/angular-clarity/src/app/stores/root/octiga/country-codes/actions';
import { Iso3166_2Country } from 'projects/angular-clarity/src/app/stores/root/octiga/country-codes/model';

@Component({
    selector: 'app-create-country-named-location',
    templateUrl: './create-country-named-location.component.html',
    styleUrls: ['./create-country-named-location.component.scss'],
})
export class CreateCountryNamedLocationComponent implements OnInit {
    @ViewChild('selectionList') selectionList!: MatSelectionList;

    form: FormGroup;
    countries$ = this.store.select(selectIso3166_2CountryCodes);
    countries: Iso3166_2Country[] = [];
    filteredCountries: Iso3166_2Country[] = [];
    isCreating = false;
    @Input() tenantId;
    @Input() itemToBeUpdated: CountryNamedLocation;

    @Output() close = new EventEmitter<void>();

    constructor(
        private store: Store<any>,
        private fb: FormBuilder,
        private actions$: Actions,
        private snackbar: SnackbarService,
    ) {}

    ngOnInit(): void {
        this.form = this.initForm();
        this.store.dispatch(loadIso3166_2CountryCodes());

        this.countries$.subscribe((data) => {
            this.countries = data;
            this.filteredCountries = data;
        });
    }

    onSearchChange() {
        const lowerSearch = this.form.get('searchText').value?.toLowerCase();
        this.filteredCountries = this.countries.filter(
            (item) => item.name.toLowerCase().includes(lowerSearch) || item.code.toLowerCase().includes(lowerSearch),
        );
    }

    initForm(): FormGroup {
        return this.fb.group({
            displayName: [this.itemToBeUpdated?.displayName, Validators.required],
            countryLookupMethod: [this.itemToBeUpdated?.countryLookupMethod || 'clientIpAddress', Validators.required],
            countriesAndRegions: [this.itemToBeUpdated?.countriesAndRegions || [], Validators.required],
            includeUnknownCountriesAndRegions: [this.itemToBeUpdated?.includeUnknownCountriesAndRegions || false],
            searchText: [''],
        });
    }

    save() {
        this.isCreating = true;

        const location = {
            '@odata.type': '#microsoft.graph.countryNamedLocation',
            displayName: this.form.value.displayName,
            countryLookupMethod: this.form.value.countryLookupMethod,
            countriesAndRegions: this.form.value.countriesAndRegions,
            includeUnknownCountriesAndRegions: this.form.value.includeUnknownCountriesAndRegions,
        };

        if (this.itemToBeUpdated?.id) {
            this.update(location);
        } else {
            this.create(location);
        }
    }

    sortSelected() {
        this.filteredCountries = this.filteredCountries.sort((res) => {
            const selectedItems = this.form.value.countriesAndRegions;
            const selected = selectedItems.some((selected_item) => selected_item === res.code);
            return selected ? -1 : 1;
        });
    }

    create(location: CountryNamedLocation) {
        this.store.dispatch(createNamedLocation({ _tenant: this.tenantId, location }));

        this.actions$.pipe(ofType(createNamedLocationSuccess)).subscribe((_) => {
            this.isCreating = false;
            this.snackbar.success('Country named location has been added successfully');
            this.close.emit();
        });

        this.actions$.pipe(ofType(createNamedLocationFailure)).subscribe((error) => {
            this.isCreating = false;

            this.snackbar.error(error.error.message);
        });
    }

    update(location: CountryNamedLocation) {
        location.id = this.itemToBeUpdated.id;
        this.store.dispatch(updateNamedLocation({ _tenant: this.tenantId, location }));

        this.actions$.pipe(ofType(updateNamedLocationSuccess)).subscribe((_) => {
            this.isCreating = false;
            this.snackbar.success('Country named location has been updated successfully');
            this.close.emit();
        });

        this.actions$.pipe(ofType(updateNamedLocationFailure)).subscribe((error) => {
            this.isCreating = false;
            this.snackbar.error(error.error.message);
        });
    }

    delete() {
        this.store.dispatch(deleteNamedLocation({ _tenant: this.tenantId, id: this.itemToBeUpdated.id }));

        this.actions$.pipe(ofType(deleteNamedLocationSuccess)).subscribe((_) => {
            this.snackbar.success('Ip named location has been deleted successfully');
            this.close.emit();
        });

        this.actions$.pipe(ofType(deleteNamedLocationFailure)).subscribe((error) => {
            this.snackbar.error(error.error.message);
        });
    }

    isAllSelected(): boolean {
        if (!this.selectionList) return false;
        const options = this.selectionList.options.toArray();
        return options.every((option) => option.selected);
    }

    toggleSelectAll(): void {
        const options = this.selectionList.options.toArray();
        const selectAll = !this.isAllSelected();

        options.forEach((option) => {
            option.selected = selectAll;
        });
    }

    onCancel() {
        this.close.emit();
    }
    onCreate() {
        this.close.emit(undefined);
    }
}
